import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { db, isConfigured } from '../firebaseConfig';
import { collection, getDocs, deleteDoc, doc, updateDoc, orderBy, query } from 'firebase/firestore';

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (isConfigured) {
      fetchMessages();
    }
  }, []);

  const fetchMessages = async () => {
    if (!isConfigured) return;
    try {
      const q = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const messagesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesList);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await updateDoc(doc(db, 'messages', id), {
        status: 'read',
        readAt: new Date().toISOString()
      });
      fetchMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAsUnread = async (id) => {
    try {
      await updateDoc(doc(db, 'messages', id), {
        status: 'unread',
        readAt: null
      });
      fetchMessages();
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteDoc(doc(db, 'messages', id));
        alert('Message deleted successfully!');
        setSelectedMessage(null);
        fetchMessages();
      } catch (error) {
        console.error('Error deleting message:', error);
        alert('Error deleting message. Please try again.');
      }
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'unread') return msg.status === 'unread';
    if (filter === 'read') return msg.status === 'read';
    return true;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-7xl mx-auto'>
          {!isConfigured && (
            <div className='bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded'>
              <p className='font-bold'>⚠️ Firebase Not Configured</p>
            </div>
          )}

          {/* Header */}
          <div className='flex justify-between items-center mb-6'>
            <div>
              <h1 className='text-3xl font-bold text-white'>MESSAGES</h1>
              <p className='text-white text-sm mt-1'>
                Contact form submissions
                {unreadCount > 0 && (
                  <span className='ml-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs'>
                    {unreadCount} new
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className='bg-white rounded-lg p-4 mb-6 shadow-lg'>
            <div className='flex gap-2 flex-wrap'>
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-[#FFB7CE] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({messages.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'unread'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'read'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Read ({messages.filter(m => m.status === 'read').length})
              </button>
            </div>
          </div>

          {/* Messages List */}
          {filteredMessages.length === 0 ? (
            <div className='bg-white rounded-lg p-12 text-center shadow-lg'>
              <svg className='w-16 h-16 mx-auto mb-4 text-gray-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
              </svg>
              <p className='text-gray-500 text-lg font-medium'>No messages found</p>
              <p className='text-gray-400 text-sm mt-2'>
                {filter === 'unread' 
                  ? 'All messages have been read'
                  : filter === 'read'
                  ? 'No read messages yet'
                  : 'No messages received yet'}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              {/* Messages List */}
              <div className='lg:col-span-1 space-y-3'>
                {filteredMessages.map((message) => (
                  <div
                    key={message.id}
                    onClick={() => {
                      setSelectedMessage(message);
                      if (message.status === 'unread') {
                        handleMarkAsRead(message.id);
                      }
                    }}
                    className={`bg-white rounded-lg p-4 shadow cursor-pointer hover:shadow-lg transition-all duration-200 ${
                      selectedMessage?.id === message.id ? 'ring-2 ring-[#FFB7CE]' : ''
                    } ${message.status === 'unread' ? 'border-l-4 border-red-500' : ''}`}
                  >
                    <div className='flex justify-between items-start mb-2'>
                      <h3 className='font-bold text-gray-800'>{message.fullName || `${message.firstName} ${message.lastName}`}</h3>
                      {message.status === 'unread' && (
                        <span className='bg-red-500 text-white px-2 py-1 rounded-full text-xs'>New</span>
                      )}
                    </div>
                    <p className='text-sm text-gray-600 mb-2'>{message.email}</p>
                    <p className='text-xs text-gray-500 line-clamp-2'>{message.message}</p>
                    <p className='text-xs text-gray-400 mt-2'>{formatDate(message.createdAt)}</p>
                  </div>
                ))}
              </div>

              {/* Message Detail */}
              <div className='lg:col-span-2'>
                {selectedMessage ? (
                  <div className='bg-white rounded-lg p-6 shadow-lg'>
                    <div className='flex justify-between items-start mb-6'>
                      <div>
                        <h2 className='text-2xl font-bold text-gray-800 mb-2'>
                          {selectedMessage.fullName || `${selectedMessage.firstName} ${selectedMessage.lastName}`}
                        </h2>
                        <div className='space-y-1 text-sm text-gray-600'>
                          <p className='flex items-center gap-2'>
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                            </svg>
                            <a href={`mailto:${selectedMessage.email}`} className='hover:text-[#FFB7CE]'>
                              {selectedMessage.email}
                            </a>
                          </p>
                          <p className='flex items-center gap-2'>
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' />
                            </svg>
                            <a href={`tel:${selectedMessage.phone}`} className='hover:text-[#FFB7CE]'>
                              {selectedMessage.phone}
                            </a>
                          </p>
                          <p className='text-xs text-gray-400 mt-2'>
                            Received: {formatDate(selectedMessage.createdAt)}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          selectedMessage.status === 'unread'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {selectedMessage.status === 'unread' ? '● Unread' : '✓ Read'}
                      </span>
                    </div>

                    <div className='border-t border-gray-200 pt-6 mb-6'>
                      <h3 className='font-semibold text-gray-700 mb-3'>Message:</h3>
                      <p className='text-gray-700 leading-relaxed whitespace-pre-wrap'>
                        {selectedMessage.message}
                      </p>
                    </div>

                    <div className='flex gap-3 pt-4 border-t border-gray-200'>
                      <button
                        onClick={() => window.location.href = `mailto:${selectedMessage.email}`}
                        className='flex-1 bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200'
                      >
                        Reply via Email
                      </button>
                      {selectedMessage.status === 'read' ? (
                        <button
                          onClick={() => handleMarkAsUnread(selectedMessage.id)}
                          className='px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200'
                        >
                          Mark Unread
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMarkAsRead(selectedMessage.id)}
                          className='px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200'
                        >
                          Mark Read
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(selectedMessage.id)}
                        className='px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200'
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className='bg-white rounded-lg p-12 text-center shadow-lg h-full flex items-center justify-center'>
                    <div>
                      <svg className='w-16 h-16 mx-auto mb-4 text-gray-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' />
                      </svg>
                      <p className='text-gray-500'>Select a message to view details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}